/**
 * Compute a 0-100 health score from smart_report counts.
 * Weighted: normal (+1), borderline (-1.5 each ≈ 6 points), needing_attention (-4 each ≈ 8 points).
 * Empirical formula that produces the same "76 = Good" bands as the reference design.
 */
export function computeHealthScore(smart) {
  if (!smart) return null;
  const need = Number(smart.parameters_needing_attention || 0);
  const bord = Number(smart.parameters_borderline || 0);
  const norm = Number(smart.parameters_normal || 0);
  const total = need + bord + norm;
  if (total === 0) return null;
  const raw = norm * 1.0 + bord * 0.55 + need * 0.15;
  const score = Math.max(15, Math.min(99, Math.round((raw / total) * 100)));
  return score;
}

export function scoreBand(score) {
  if (score === null || score === undefined) return { label: "—", color: "#94a3b8" };
  if (score >= 85) return { label: "Excellent", color: "#4CAF50" };
  if (score >= 70) return { label: "Good Health", color: "#4CAF50" };
  if (score >= 55) return { label: "Fair", color: "#F5C400" };
  if (score >= 40) return { label: "Needs Attention", color: "#E55A25" };
  return { label: "Critical", color: "#E53935" };
}

export function statusColor(status) {
  // 4-colour palette used everywhere the report shows a status pill / hotspot:
  //   • green  #16A34A — normal
  //   • orange #F59E0B — watch (borderline / concern / low)
  //   • red    #EF4444 — attention (high / critical)
  //   • grey   #94A3B8 — not tested / unknown
  const s = String(status || "").toLowerCase();
  if (s === "normal") return "#16A34A";
  if (s === "high" || s === "critical" || s === "attention") return "#EF4444";
  if (s === "borderline" || s === "concern" || s === "low" || s === "watch") return "#F59E0B";
  return "#94A3B8";
}

/** Parse a numeric reference range like "70 - 100 mg/dl" or "<200" → {min,max}. */
export function parseRange(ref) {
  if (!ref) return null;
  const m = String(ref).match(/([\d.]+)\s*[–-]\s*([\d.]+)/);
  if (m) return { min: parseFloat(m[1]), max: parseFloat(m[2]) };
  const lt = String(ref).match(/<\s*([\d.]+)/);
  if (lt) return { min: 0, max: parseFloat(lt[1]) };
  const gt = String(ref).match(/>\s*([\d.]+)/);
  if (gt) return { min: parseFloat(gt[1]), max: parseFloat(gt[1]) * 2 };
  return null;
}

/** Compute the % position (0-100) of `value` inside a range shown with 25% headroom on each side. */
export function rangePosition(value, range) {
  if (!range || value == null || isNaN(value)) return null;
  const span = Math.max(0.0001, range.max - range.min);
  const visStart = range.min - span * 0.35;
  const visEnd = range.max + span * 0.35;
  const pct = ((value - visStart) / (visEnd - visStart)) * 100;
  return Math.max(2, Math.min(98, pct));
}
